/**
 * Admin Users Screen.
 * Lists users with search, filter, and detail modal.
 */

import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/app/PageHeader';
import { LoadingOverlay } from '@/app/Spinner';
import { EmptyState } from '@/app/EmptyState';
import { Pagination } from '@/app/Pagination';
import { Modal } from '@/app/Modal';
import { classifyError } from '@/api/errors';
import * as usersApi from '@/api/usersApi';
import type { User } from '@/api/types';

export function UsersScreen() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const limit = 20;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await usersApi.listUsers({
        search: search || undefined,
        status: statusFilter || undefined,
        limit,
        cursor: page > 1 ? String((page - 1) * limit) : undefined,
      });
      setUsers(result.data);
      setTotal(result.pagination.total);
      setHasMore(result.pagination.has_more);
    } catch (err) {
      setError(classifyError(err).message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleStatusChange = (status: string) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleDeleteUser = async (userId: string, username: string) => {
    if (
      !window.confirm(
        `Delete user "${username}"?\n\nThis action is permanent and cannot be undone.`,
      )
    ) {
      return;
    }
    setActionLoading(true);
    try {
      await usersApi.deleteUser(userId);
      await fetchUsers();
      if (selectedUser?.id === userId) {
        setSelectedUser(null);
      }
    } catch (err) {
      alert(classifyError(err).message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspendUser = async (userId: string, username: string) => {
    if (
      !window.confirm(
        `Suspend user "${username}"?\n\nThe user will not be able to log in or use the service.`,
      )
    ) {
      return;
    }
    setActionLoading(true);
    try {
      await usersApi.updateUser(userId, { status: 'suspended' });
      await fetchUsers();
      if (selectedUser?.id === userId) {
        setSelectedUser({ ...selectedUser, status: 'suspended' });
      }
    } catch (err) {
      alert(classifyError(err).message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivateUser = async (userId: string) => {
    setActionLoading(true);
    try {
      await usersApi.updateUser(userId, { status: 'active' });
      await fetchUsers();
      if (selectedUser?.id === userId) {
        setSelectedUser({ ...selectedUser, status: 'active' });
      }
    } catch (err) {
      alert(classifyError(err).message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="page-content">
      <PageHeader title="Users" subtitle={`${total.toLocaleString()} total users`} />

      {/* Filter Bar */}
      <div className="filter-bar">
        <form
          onSubmit={handleSearch}
          className="search-input"
          style={{ display: 'flex', gap: '8px', flex: 1 }}
        >
          <div style={{ position: 'relative', flex: 1 }}>
            <SearchIcon />
            <input
              type="text"
              className="input"
              placeholder="Search by username or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: '36px', width: '100%' }}
            />
          </div>
          <button type="submit" className="btn btn--primary">
            Search
          </button>
        </form>

        <select
          className="select"
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="deleted">Deleted</option>
        </select>
      </div>

      {/* Error Banner */}
      {error && <div className="alert alert--error mb-16">{error}</div>}

      {/* Loading / Empty */}
      {loading ? (
        <LoadingOverlay />
      ) : users.length === 0 ? (
        <EmptyState message="No users found matching your criteria." />
      ) : (
        <>
          <div className="card">
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Reports</th>
                    <th>Joined</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => setSelectedUser(user)}
                          style={{ textAlign: 'left' }}
                        >
                          {user.username}
                        </button>
                      </td>
                      <td className="muted">{user.email ?? '—'}</td>
                      <td>
                        <span className={`pill pill--${user.status}`}>{user.status}</span>
                      </td>
                      <td>{user.report_count ?? '—'}</td>
                      <td className="muted text-sm">{formatDate(user.created_at)}</td>
                      <td className="muted text-sm">{formatDate(user.last_login_at)}</td>
                      <td>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => setSelectedUser(user)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            page={page}
            limit={limit}
            total={total}
            hasMore={hasMore}
            onPageChange={setPage}
          />
        </>
      )}

      {/* User Detail Modal */}
      <Modal
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        title="User Details"
        footer={
          <>
            {selectedUser?.status === 'active' && (
              <button
                className="btn btn--danger"
                onClick={() =>
                  selectedUser && handleSuspendUser(selectedUser.id, selectedUser.username)
                }
                disabled={actionLoading}
              >
                Suspend User
              </button>
            )}
            {selectedUser?.status === 'suspended' && (
              <button
                className="btn btn--primary"
                onClick={() => selectedUser && handleActivateUser(selectedUser.id)}
                disabled={actionLoading}
              >
                Activate User
              </button>
            )}
            <button
              className="btn btn--danger-outline"
              onClick={() =>
                selectedUser && handleDeleteUser(selectedUser.id, selectedUser.username)
              }
              disabled={actionLoading}
            >
              Delete User
            </button>
            <button className="btn btn--ghost" onClick={() => setSelectedUser(null)}>
              Close
            </button>
          </>
        }
      >
        {selectedUser && (
          <div className="stack">
            <div className="row gap-16 items-center mb-16">
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: 'var(--color-bg-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                  fontWeight: 700,
                }}
              >
                {selectedUser.username[0].toUpperCase()}
              </div>
              <div>
                <div className="font-bold text-lg">{selectedUser.username}</div>
                <span className={`pill pill--${selectedUser.status}`}>{selectedUser.status}</span>
              </div>
            </div>

            <div className="field">
              <label>User ID</label>
              <input className="input" value={selectedUser.id} readOnly />
            </div>

            <div className="field">
              <label>Email</label>
              <input className="input" value={selectedUser.email ?? '—'} readOnly />
            </div>

            <div className="field">
              <label>Email Verified</label>
              <input
                className="input"
                value={selectedUser.email_verified ? 'Yes' : 'No'}
                readOnly
              />
            </div>

            <div className="field">
              <label>Phone</label>
              <input className="input" value={selectedUser.phone ?? '—'} readOnly />
            </div>

            <div className="field">
              <label>Bio</label>
              <textarea
                className="textarea"
                value={selectedUser.bio ?? '—'}
                readOnly
                style={{ minHeight: 80 }}
              />
            </div>

            <div className="grid-2">
              <div className="field">
                <label>Created At</label>
                <input className="input" value={formatDate(selectedUser.created_at)} readOnly />
              </div>
              <div className="field">
                <label>Last Login</label>
                <input className="input" value={formatDate(selectedUser.last_login_at)} readOnly />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      style={{
        position: 'absolute',
        left: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 16,
        height: 16,
        color: 'var(--color-text-muted)',
      }}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
